import {Location} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {AuthorizationError} from 'app/core/error-handler/errors/authorization.error';
import {GraphQLValidationError} from 'app/core/error-handler/errors/grahpql/validation-error';
import {AuthService} from 'app/core/services/auth.service';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, shareReplay, take} from 'rxjs/operators';
import {InternalError} from '../error-handler/errors/internal-error';
import {NoConnectionError} from '../error-handler/errors/network/no-connection-error';
import {REDIRECT_TO_LEGACY_LOGIN} from 'app/core/helpers/redirect';
import {sleep} from 'test-helpers/utils';

declare const document;

@Injectable({
    providedIn: 'root',
})
export class RouterHelperService {
    private navigationCycleDone$ = new Subject();
    private navInProgress;
    private forceRefreshPage = false;

    private navigationErrors$;

    private handleNextNavigationError = false;
    private previousRoutePath = '';

    public readonly routeChanges$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged((a: NavigationEnd, b: NavigationEnd) => a.url.split('?')[0] === b.url.split('?')[0]),
        shareReplay(1)
    );

    constructor(
        private router: Router,
        private location: Location,
        private auth: AuthService,
        @Inject(REDIRECT_TO_LEGACY_LOGIN) private redirectToLogin: () => void
    ) {
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.forceRefreshPage) {
                    this.redirect(event);
                }
                this.navInProgress = true;
            }
            if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
                this.navInProgress = false;
                this.navigationCycleDone$.next(event);
            }
            if (event instanceof NavigationEnd) {
                this.previousRoutePath = event.urlAfterRedirects;
            }
        });

        this.navigationErrors$ = router.events.pipe(
            filter(event => event instanceof NavigationError),
            debounceTime(10)
        );

        this.navigationErrors$.subscribe((e: NavigationError) => {
            console.warn(e);
            if (e.error.name === 'ChunkLoadError') {
                return this.handleChunkLoadError(e);
            }
            if (e && e.error) {
                console.warn(e.error);
                this.checkUnAuthorized(e.error);
            }
            if (this.handleNextNavigationError) {
                this.handleNextNavigationError = false;
                // update url
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                router.navigated ? this.location.go(e.url) : this.location.replaceState(e.url);
                // show error page
                if (e.error) {
                    this.resolverErrorHandler(e.error);
                }
            }
        });

        router.errorHandler = () => {
            this.handleNextNavigationError = true;
            return true;
        };
    }

    private actions = [];

    /* istanbul ignore next */
    redirect(event: NavigationStart) {
        document.location = event.url;
    }

    getPreviousPath(): string {
        return this.previousRoutePath;
    }

    private performAction() {
        if (this.actions.length) {
            let cb = this.actions[0];
            cb();
            this.actions = this.actions.slice(1);
            this.bindNavigationEnd();
        }
    }

    private bindNavigationEnd() {
        if (this.navInProgress) {
            return this.navigationCycleDone$.pipe(take(1)).subscribe(() => this.performAction());
        }
        this.performAction();
    }

    private performAfterNavigate(cb: () => void) {
        this.actions.push(cb);
        this.bindNavigationEnd();
    }

    public navigateTo500() {
        this.performAfterNavigate(() =>
            this.router.navigateByUrl('/500', {skipLocationChange: true}).then(() => (this.forceRefreshPage = true))
        );
    }

    public navigateToNotFound() {
        this.performAfterNavigate(() => this.router.navigateByUrl('/404', {skipLocationChange: true}));
    }

    public navigateToNoInternetConnection() {
        this.performAfterNavigate(() =>
            this.router.navigateByUrl('/check-internet-connection', {skipLocationChange: true}).then(() => (this.forceRefreshPage = true))
        );
    }

    public initialNavigation(err) {
        if (err) {
            this.hideGlobalPreloader();
            return this.handleNetworkError(err);
        }
        this.router.initialNavigation();
        this.performAfterNavigate(() => setTimeout(() => this.hideGlobalPreloader()));
    }

    public handleNetworkError = (err: InternalError) => {
        this.checkUnAuthorized(err);
        if (err && typeof err.isCritical === 'function' && err.isCritical()) {
            this.navigateTo500();
        } else if (err instanceof NoConnectionError) {
            // if empty status user just have no internet connection
            this.navigateToNoInternetConnection();
        } else {
            this.navigateTo500();
        }
    };

    public resolverErrorHandler = (err: InternalError) => {
        if (err instanceof GraphQLValidationError) {
            this.navigateToNotFound();
        } else {
            this.handleNetworkError(err);
        }
    };

    checkUnAuthorized(err) {
        if (err instanceof AuthorizationError) {
            console.warn('checkUnAuthorized AuthorizationError');
            this.auth.setCurrentUser(null);
            return this.redirectToLogin();
        }
    }

    public hideGlobalPreloader() {
        const preloader = document.body.querySelector('.GlobalPreloader');
        if (preloader) {
            preloader.classList.add('hidden');
            setTimeout(() => (preloader.style.display = 'none'), 1000);
        }
    }

    public navigateBack(defaultUrl: any[]): void {
        let referer = document.referrer ? new URL(document.referrer) : null;
        if (referer && location.hostname === referer.hostname) {
            history.back();
        } else {
            this.router.navigate(defaultUrl);
        }
    }

    async handleChunkLoadError(e: NavigationError) {
        await sleep(100);
        if (e.url.includes('reloadTime=')) {
            throw new Error(e.error);
        }

        // reload page
        const chunks = e.url.split('?');
        chunks[1] = 'reloadTime=' + Date.now() + '&' + (chunks[1] || '');
        document.location.href = chunks.join('?');
    }
}
